import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tool", "prompt", "copyLabel"]
  static values = {
    endpointUrl: String,
    honeypotField: String
  }

  connect() {
    this.generate()
  }

  generate() {
    this.promptTarget.value = this.promptFor(this.toolTarget.value)
    this.resetCopyLabel()
  }

  copy() {
    const text = this.promptTarget.value

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => this.showCopied())
      return
    }

    this.promptTarget.select()
    document.execCommand("copy")
    this.showCopied()
  }

  promptFor(tool) {
    const endpointUrl = this.endpointUrlValue
    const honeypotField = this.honeypotFieldValue?.trim()
    const toolInstruction = this.toolInstruction(tool)
    const honeypotGuidance = honeypotField
      ? `- Add a honeypot spam field as a hidden or visually hidden input with name="${honeypotField}"
- The honeypot field must be present but left empty by real visitors
- Do not validate the honeypot field as required and do not show it in the visible UI`
      : `- Formserve supports honeypot spam fields when configured in endpoint settings
- If this project already has a hidden honeypot field, preserve it and keep it empty for real visitors
- Do not invent a new honeypot field name unless the same field name is also configured in Formserve endpoint settings`

    return `${toolInstruction}

Update the existing contact, waitlist, newsletter, or lead capture form in this project to submit to Formserve.

Use this endpoint:
${endpointUrl}

Requirements:
- Set the form method to POST
- Set the form action to ${endpointUrl}
- Preserve the existing design and styling
- Keep existing fields unless they are broken
- Ensure fields have proper name attributes
- Add required validation for email fields
- Include honeypot spam protection guidance from the section below
- Show a success message after submission
- Show a friendly error message if submission fails
- Do not create a custom backend API route for this form unless absolutely necessary
- Do not expose secrets or API keys in frontend code
- Do not add unrelated dependencies or redesign the form

Field guidance:
- Keep existing field names when possible
- Use name="email" for email fields when one exists
- Use name="name" for name fields when one exists
- Use name="message" for message or textarea fields when one exists
- Any additional fields can be submitted as normal named form inputs

Honeypot spam protection:
${honeypotGuidance}

Success message guidance:
- After a successful submission, show a clear inline confirmation such as "Thanks, your message has been sent."
- Keep the user on the same page unless the project already uses a different success flow

Error message guidance:
- If submission fails, show a friendly inline error and allow the user to retry
- Do not expose raw technical errors to visitors

If the project uses React, Next.js, Vue, Astro, or plain HTML, apply the most appropriate implementation for that stack.`
  }

  toolInstruction(tool) {
    const instructions = {
      claude_code: "Scan the project and update the most relevant form component.",
      codex: "Make the minimal safe code change and explain the changed files.",
      cursor: "Update the current form component and preserve the existing UI.",
      lovable: "Modify the generated website form so it submits to Formserve.",
      bolt: "Update the app form and test the submission behavior.",
      replit: "Apply the change in the current Replit project.",
      generic: "Update the project form using a framework-agnostic implementation."
    }

    return instructions[tool] || instructions.generic
  }

  showCopied() {
    this.copyLabelTarget.textContent = "Copied"
    window.setTimeout(() => this.resetCopyLabel(), 1500)
  }

  resetCopyLabel() {
    this.copyLabelTarget.textContent = "Copy Prompt"
  }
}
